import React, { useEffect, useState } from "react";
import PageHelp from "@/components/PageHelp";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, Loader2, Trash2, ShieldAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function PlatformAdmin() {
  const [owners, setOwners] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [delFor, setDelFor] = useState(null);

  const load = () => api.get("/platform/owners").then((r) => setOwners(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/platform/owners", form);
      toast.success("Inhaber eingeladen – E-Mail versendet");
      setOpen(false); setForm({ email: "", name: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const setActive = async (o, active) => {
    try { await api.put(`/platform/owners/${o.user_id}`, { active }); toast.success(active ? "Konto aktiviert" : "Konto deaktiviert"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async () => {
    try { await api.delete(`/platform/owners/${delFor.user_id}`); toast.success("Arbeitsbereich gelöscht"); setDelFor(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="platform-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-head font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Building2 className="h-8 w-8 text-primary" />Plattform-Verwaltung <PageHelp id="plattform" title="Plattform-Verwaltung" text="Laden Sie Arbeitsbereich-Inhaber ein und verwalten Sie deren Konten. Sie erhalten dabei keinen Einblick in die Inhalte fremder Arbeitsbereiche." /></h1>
          <p className="text-slate-500 mt-1">Arbeitsbereich-Inhaber einladen und verwalten. Kein Einblick in deren Inhalte.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="h-11" data-testid="invite-owner-btn"><Plus className="h-4 w-4 mr-2" />Inhaber einladen</Button></DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-head text-xl">Neuen Arbeitsbereich-Inhaber einladen</DialogTitle></DialogHeader>
            <form onSubmit={invite} className="space-y-4 mt-2">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="owner-name-input" /></div>
              <div><Label>E-Mail *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="owner-email-input" /></div>
              <p className="text-xs text-slate-500">Die Person erhält eine Einladung mit Link zum Festlegen des Passworts und einen eigenen, leeren Arbeitsbereich.</p>
              <DialogFooter><Button type="submit" disabled={saving} className="w-full" data-testid="save-owner-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Einladen"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="divide-y divide-slate-100">
        {!owners ? <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          : owners.length === 0 ? <div className="p-8 text-center text-slate-400">Noch keine Inhaber.</div>
          : owners.map((o) => {
            const active = o.active !== false;
            return (
              <div key={o.user_id} className="flex items-center gap-3 p-4 flex-wrap" data-testid={`owner-row-${o.user_id}`}>
                <div className="h-10 w-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-semibold shrink-0">{(o.name || o.email).slice(0, 2).toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{o.name || o.email} {o.is_platform_admin && <Badge variant="outline" className="ml-1 bg-primary/10 text-primary border-primary/20">Verwalter</Badge>}</div>
                  <div className="text-xs text-slate-500 truncate" data-testid={`owner-meta-${o.user_id}`}>{o.email} · {o.auth_provider === "google" ? "Google-Login" : "Passwort-Login"} · {o.project_count ?? 0} Projekte · {o.team_size || 1} Konto/Konten · seit {(o.created_at || "").slice(0, 10)}</div>
                </div>
                {!active && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Deaktiviert</Badge>}
                {!o.is_platform_admin && (
                  <>
                    <div className="flex items-center gap-2"><span className="text-xs text-slate-500">Aktiv</span>
                      <Switch checked={active} onCheckedChange={(v) => setActive(o, v)} data-testid={`owner-active-${o.user_id}`} /></div>
                    <button onClick={() => setDelFor(o)} data-testid={`delete-owner-${o.user_id}`} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500" title="Arbeitsbereich löschen"><Trash2 className="h-4 w-4" /></button>
                  </>
                )}
              </div>
            );
          })}
      </Card>

      <AlertDialog open={!!delFor} onOpenChange={(o) => !o && setDelFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-red-500" />Arbeitsbereich unwiderruflich löschen?</AlertDialogTitle>
            <AlertDialogDescription>Der gesamte Arbeitsbereich von <b>{delFor?.name || delFor?.email}</b> – alle Projekte, Gebäude, Pläne, Mängel, Fotos, Berichte und Team-Konten – wird endgültig gelöscht. Dies kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-owner-btn">Endgültig löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
